import { START_DATE_ALIAS_FIELD_ID } from '@atlassian/jira-business-constants';
import { ff } from '@atlassian/jira-feature-flagging';
import {
	ASSIGNEE_TYPE,
	DUE_DATE_TYPE,
	ISSUE_TYPE,
	STATUS_TYPE,
	SUMMARY_TYPE,
} from '@atlassian/jira-platform-field-config';
import { toDateOnly } from '@atlassian/jira-platform-utils-date-only';
import type {
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges as ItemEdge,
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_issueLinks_edges as IssueLinksEdges,
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields as Field,
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraIssueTypeField as IssueTypeField,
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraStatusField as StatusField,
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraSingleLineTextField as SingleLineTextField,
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraDatePickerField as DatePickerField,
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields_JiraSingleSelectUserPickerField as SingleSelectUserPickerField,
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_childSummary as ChildSummary,
} from './__generated_apollo__/JWMTimelineViewItems';
import type { TransformedIssueLinks, TimelineViewIssue, TimelineViewIssuesResult } from './types';

const transformField = (field?: Field | null) => {
	if (!field) return {};

	const fieldId = field?.aliasFieldId || field?.fieldId;
	switch (fieldId) {
		case 'issuekey': // not available to be imported from jira-platform-field-config
			return {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				key: (field as SingleLineTextField).text,
			};

		case SUMMARY_TYPE:
			return {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				summary: (field as SingleLineTextField)?.text,
			};

		case STATUS_TYPE: {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const { status } = field as StatusField;
			return {
				status: {
					id: status.statusId,
					name: status.name,
					categoryId: status.statusCategory?.statusCategoryId,
				},
			};
		}

		case ISSUE_TYPE: {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const { issueType } = field as IssueTypeField;
			return {
				issueType: {
					id: issueType?.issueTypeId,
					name: issueType?.name,
					iconUrl: issueType?.avatar?.medium,
					hierarchyLevel: issueType?.hierarchy?.level ?? 0,
				},
			};
		}

		case 'rank': // not available to be imported from jira-platform-field-config
			return {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				rank: (field as SingleLineTextField)?.text,
			};

		case START_DATE_ALIAS_FIELD_ID: {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const { date } = field as DatePickerField;

			return {
				startDate: toDateOnly(date)?.toUTCDate().valueOf(),
			};
		}

		case DUE_DATE_TYPE: {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const { date } = field as DatePickerField;

			return {
				dueDate: toDateOnly(date)?.toUTCDate().valueOf(),
			};
		}

		case ASSIGNEE_TYPE: {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const { user } = field as SingleSelectUserPickerField;
			return {
				assignee:
					(user && {
						avatarUrl: user.picture,
						displayName: user.name,
						accountId: user.accountId,
					}) ??
					undefined,
			};
		}

		default:
			return {};
	}
};

const INWARD = 'INWARD';

const transformIssueLinks = (edges: (IssueLinksEdges | null)[]): TransformedIssueLinks[] | [] => {
	if (edges == null || !Array.isArray(edges) || edges.length === 0) return [];

	return edges
		.filter((edge) => edge?.node)
		.map((edge) => {
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const { issueLinkId, direction, relationName, issue } = edge!.node!;

			return {
				issueLinkId: issueLinkId || null,
				type: {
					inward: direction === INWARD ? relationName || '' : '',
					outward: direction === INWARD ? '' : relationName || '',
				},
				inward: direction === INWARD ? { issueId: issue?.issueId ?? null } : null,
				outward: direction === INWARD ? null : { issueId: issue?.issueId ?? null },
			};
		});
};

const emptyArray: TimelineViewIssue[] = [];

export const transformResult = (edges: (ItemEdge | null)[]): TimelineViewIssuesResult => {
	if (edges == null || !Array.isArray(edges)) {
		return { issues: [] };
	}

	const issues = edges.reduce((transformedData: TimelineViewIssue[], edge) => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const populateItem = (acc: TimelineViewIssue, field?: any | null) => ({
			...acc,
			...transformField(field),
		});

		if (edge && edge.node && Array.isArray(edge.node?.fields)) {
			const issueLinkEdges = edge.node?.issueLinks?.edges || emptyArray;

			const item: TimelineViewIssue = edge.node.fields.reduce(populateItem, {
				id: edge.node.id,
				issueId: String(edge.node.issueId),
				hasChildren: !!edge.node?.childSummary?.hasChildren,
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				issueLinks: transformIssueLinks(issueLinkEdges as IssueLinksEdges[]),
				originalIssueLinks: issueLinkEdges,
			});

			transformedData.push(item);
		}

		return transformedData;
	}, []);

	return {
		issues,
	};
};

export const addChildToParentTimeline = <
	NodeType extends {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		readonly fields?: ReadonlyArray<any> | null;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		readonly issueId: any;
		readonly childSummary?: ChildSummary | null;
	},
>(
	parentIssue: NodeType,
): NodeType => ({
	...parentIssue,
	childSummary: {
		__typename: 'JiraWorkManagementChildSummary',
		hasChildren: true,
	},
});

const getIssueFieldByName = (edge: ItemEdge | null, targetField: string) =>
	edge?.node?.fields?.find((field) => (field?.aliasFieldId || field?.fieldId) === targetField);

const issueIsSubTask = (edge: ItemEdge | null) =>
	// @ts-expect-error - ts-2339 Property 'issueType' does not exist on type 'JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields'.
	getIssueFieldByName(edge, 'issuetype')?.issueType?.name === 'Sub-task';

const getParentRank = (edge: ItemEdge | null) =>
	// @ts-expect-error - ts-2339 Property 'issueType' does not exist on type 'JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields'.
	getIssueFieldByName(edge, 'parent')?.parentIssue?.rankField?.[0].text ?? '';

export const rankSorter = (edge: ItemEdge | null) => {
	// @ts-expect-error - ts-2339 Property 'issueType' does not exist on type 'JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node_fields'.
	const rank = getIssueFieldByName(edge, 'rank')?.text;
	if (ff('jwm-timeline-sort-parent-rank-first_waal4')) {
		if (issueIsSubTask(edge)) {
			const parentRank = getParentRank(edge);
			return parentRank.concat(rank ?? '');
		}
	}
	return rank;
};
